/**
 * GodModePanel — Remote Quote admin drawer.
 *
 * Security notes:
 * - God mode check uses SECURITY DEFINER RPC — cannot be spoofed from client.
 * - Simulation is purely cosmetic/local — no DB writes except explicit
 *   "Add credits" and "Set tier" actions.
 * - The god_mode_users table has no client SELECT policy — it cannot be
 *   enumerated by any client query.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../../supabaseClient";
import { useToast } from "../../hooks/useToast";

// ── Gold palette ───────────────────────────────────────────────────────────────
const G = {
  gold: "#C9A84C",
  goldBright: "#F0C040",
  goldDim: "rgba(201,168,76,0.5)",
  goldFaint: "rgba(201,168,76,0.15)",
  goldBorder: "rgba(201,168,76,0.3)",
  bg: "#0A0A0A",
  bgHeader: "linear-gradient(135deg, #1a1400, #2a1f00)",
  surface: "#111111",
  surfaceHover: "#1a1a1a",
  text: "#ffffff",
  muted: "rgba(255,255,255,0.45)",
};

const TIERS = [
  { id: "free",         label: "Remote Quote Tradie",    sub: "Tradie" },
  { id: "trial",        label: "Remote Quote Trial",     sub: "Trialing" },
  { id: "sole_trader",  label: "Remote Quote Pro",       sub: "Pro" },
  { id: "professional", label: "Remote Quote Ultimate",  sub: "Ultimate" },
  { id: "enterprise",   label: "Remote Quote Enterprise",sub: "Enterprise" },
];

// ── Accordion section ──────────────────────────────────────────────────────────
function AccordionSection({ title, icon, open, onToggle, children }) {
  return (
    <div style={{ borderBottom: `1px solid ${G.goldBorder}` }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 20px", background: "none", border: "none", cursor: "pointer",
          color: G.text, fontSize: "0.88rem", fontWeight: 600,
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>{icon}</span>
          <span>{title}</span>
        </span>
        <span style={{ color: G.goldDim, fontSize: "0.8rem", transition: "transform 0.2s", display: "inline-block", transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
          ▾
        </span>
      </button>
      {open && (
        <div style={{ padding: "0 20px 16px" }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ── Profile search with debounce ───────────────────────────────────────────────
function ProfileSearch({ onSelect, selectedUser, placeholder = "Search by email, business or full name" }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setSearchError(null);
    if (!query.trim() || query.trim().length < 2) { setResults([]); return; }

    timerRef.current = setTimeout(async () => {
      setSearching(true);
      // god_search_profiles is SECURITY DEFINER and verifies god-mode
      // server-side. It bypasses the restrictive profiles SELECT policy
      // and joins auth.users so email is searchable too.
      const { data, error } = await supabase.rpc("god_search_profiles", {
        p_term: query.trim(),
      });
      if (error) {
        // eslint-disable-next-line no-console
        console.error("[GodMode] profile search failed:", error);
        setSearchError(error.message || "Search failed");
        setResults([]);
        setSearching(false);
        return;
      }
      setResults(data || []);
      setSearching(false);
    }, 400);

    return () => clearTimeout(timerRef.current);
  }, [query]);

  function handleSelect(user) {
    onSelect(user);
    setQuery("");
    setResults([]);
  }

  return (
    <div style={{ position: "relative" }}>
      {selectedUser ? (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: G.goldFaint, border: `1px solid ${G.goldBorder}`,
          borderRadius: "8px", padding: "10px 12px", marginBottom: "12px",
        }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "6px", color: G.text, fontWeight: 600, fontSize: "0.85rem" }}>
              {selectedUser.business_name || selectedUser.full_name || "Unnamed"}
              {!selectedUser.business_id && (
                <span style={{
                  background: "rgba(245,158,11,0.15)", color: "#F59E0B",
                  border: "1px solid rgba(245,158,11,0.3)",
                  borderRadius: "4px", padding: "1px 5px",
                  fontSize: "0.65rem", fontWeight: 700, whiteSpace: "nowrap",
                }}>
                  ⚠️ No business set
                </span>
              )}
            </div>
            {selectedUser.business_name && selectedUser.full_name && (
              <div style={{ color: G.muted, fontSize: "0.75rem", marginTop: "2px" }}>
                {selectedUser.full_name}
              </div>
            )}
            {!selectedUser.business_id && (
              <div style={{ color: "rgba(245,158,11,0.7)", fontSize: "0.72rem", marginTop: "2px" }}>
                Using profile id as fallback
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => onSelect(null)}
            style={{ background: "none", border: "none", color: G.goldDim, cursor: "pointer", fontSize: "1.1rem", lineHeight: 1 }}
          >
            ×
          </button>
        </div>
      ) : (
        <>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            style={{
              width: "100%", boxSizing: "border-box",
              background: G.surface, border: `1px solid ${G.goldBorder}`,
              borderRadius: "8px", padding: "10px 12px",
              color: G.text, fontSize: "0.85rem", outline: "none",
              marginBottom: results.length > 0 ? "0" : "0",
            }}
          />
          {query.trim().length >= 2 && (
            <div style={{
              position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10,
              background: "#161616", border: `1px solid ${G.goldBorder}`,
              borderTop: "none", borderRadius: "0 0 8px 8px", overflow: "hidden",
            }}>
              {searching && (
                <div style={{ padding: "10px 12px", color: G.muted, fontSize: "0.8rem" }}>Searching…</div>
              )}
              {searchError && !searching && (
                <div style={{ padding: "10px 12px", color: G.muted, fontSize: "0.8rem" }}>
                  Search error: {searchError}
                </div>
              )}
              {results.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); handleSelect(u); }}
                  style={{
                    display: "block", width: "100%", textAlign: "left",
                    padding: "10px 12px", background: "none", border: "none",
                    cursor: "pointer", borderBottom: `1px solid rgba(255,255,255,0.05)`,
                    color: G.text,
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = G.surfaceHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: 600, fontSize: "0.85rem" }}>
                    {u.business_name || u.full_name || u.email || "Unnamed"}
                    {!u.business_id && (
                      <span style={{
                        background: "rgba(245,158,11,0.15)", color: "#F59E0B",
                        border: "1px solid rgba(245,158,11,0.3)",
                        borderRadius: "4px", padding: "1px 5px",
                        fontSize: "0.65rem", fontWeight: 700, whiteSpace: "nowrap",
                      }}>
                        ⚠️ No business set
                      </span>
                    )}
                  </div>
                  {(u.full_name && u.business_name) || u.email ? (
                    <div style={{ color: G.muted, fontSize: "0.75rem", marginTop: "1px" }}>
                      {[u.business_name ? u.full_name : null, u.email].filter(Boolean).join(" · ")}
                    </div>
                  ) : null}
                </button>
              ))}
              {!searching && !searchError && results.length === 0 && (
                <div style={{ padding: "10px 12px", color: G.muted, fontSize: "0.8rem" }}>No results</div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Section 1: Simulate tier ───────────────────────────────────────────────────
function SimulateTierSection({ currentSimulatedTierId, onSimulateTier }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {TIERS.map((t) => {
        const isActive = currentSimulatedTierId === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onSimulateTier(isActive ? null : t.id)}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "12px 14px", borderRadius: "12px", cursor: "pointer",
              background: isActive ? G.goldFaint : G.surface,
              border: isActive ? `1px solid ${G.gold}` : `1px solid rgba(255,255,255,0.07)`,
              color: G.text, textAlign: "left", transition: "all 0.15s",
            }}
          >
            <div>
              <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>{t.label}</div>
              <div style={{ color: G.muted, fontSize: "0.75rem", marginTop: "2px" }}>{t.sub}</div>
            </div>
            {isActive && (
              <span style={{
                background: G.gold, color: "#000", borderRadius: "999px",
                padding: "2px 8px", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.06em",
              }}>
                ACTIVE
              </span>
            )}
          </button>
        );
      })}
      {currentSimulatedTierId && (
        <button
          type="button"
          onClick={() => onSimulateTier(null)}
          style={{
            padding: "8px", borderRadius: "8px", border: `1px solid ${G.goldBorder}`,
            background: "none", color: G.goldDim, cursor: "pointer", fontSize: "0.8rem",
            marginTop: "4px",
          }}
        >
          Reset to real tier
        </button>
      )}
    </div>
  );
}

// ── Section 2: Add credits ─────────────────────────────────────────────────────
function AddCreditsSection() {
  const { showToast } = useToast();
  const [selectedUser, setSelectedUser] = useState(null);
  const [amount, setAmount] = useState(500);
  const [loading, setLoading] = useState(false);

  async function handleAddCredits() {
    if (!selectedUser) {
      showToast("Select a user first.", "error");
      return;
    }
    if (!amount || amount < 1) {
      showToast("Enter a valid credit amount.", "error");
      return;
    }
    const effectiveBusinessId = selectedUser.business_id || selectedUser.id;
    setLoading(true);
    const { error } = await supabase.rpc("god_add_credits", {
      p_business_id: effectiveBusinessId,
      p_amount: Number(amount),
      p_user_id: selectedUser.id,
    });
    if (error) {
      setLoading(false);
      showToast(`Failed to add credits: ${error.message}`, "error");
      return;
    }
    const { data: wallet, error: verifyError } = await supabase
      .from("credit_wallets")
      .select("balance")
      .eq("business_id", effectiveBusinessId)
      .maybeSingle();
    setLoading(false);
    if (verifyError || !wallet) {
      showToast("Credits RPC returned OK but balance could not be verified — check SQL function params.", "error");
      return;
    }
    showToast(`✓ ${amount} credits added — new balance: ${wallet.balance.toLocaleString()}`, "success");
    setSelectedUser(null);
    setAmount(500);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <ProfileSearch onSelect={setSelectedUser} selectedUser={selectedUser} />
      <div>
        <label style={{ display: "block", color: G.muted, fontSize: "0.75rem", marginBottom: "6px" }}>
          Credits to add
        </label>
        <input
          type="number"
          value={amount}
          min={1}
          max={99999}
          onChange={(e) => setAmount(e.target.value)}
          style={{
            width: "100%", boxSizing: "border-box",
            background: G.surface, border: `1px solid ${G.goldBorder}`,
            borderRadius: "8px", padding: "10px 12px",
            color: G.text, fontSize: "0.9rem", outline: "none",
          }}
        />
      </div>
      <button
        type="button"
        onClick={handleAddCredits}
        disabled={loading || !selectedUser}
        style={{
          background: loading || !selectedUser ? "rgba(255, 138, 0,0.3)" : "linear-gradient(135deg, #CC6E00, #FF8A00)",
          color: "#fff", border: "none", borderRadius: "8px",
          padding: "11px", fontWeight: 700, fontSize: "0.88rem",
          cursor: loading || !selectedUser ? "not-allowed" : "pointer",
        }}
      >
        {loading ? "Adding…" : "Add credits"}
      </button>
    </div>
  );
}

// ── Section 3: Set subscription tier ──────────────────────────────────────────
function SetTierSection() {
  const { showToast } = useToast();
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedTierId, setSelectedTierId] = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleSetTier() {
    if (!selectedUser) {
      showToast("Select a user first.", "error");
      return;
    }
    if (!selectedTierId) {
      showToast("Select a tier first.", "error");
      return;
    }
    const effectiveBusinessId = selectedUser.business_id || selectedUser.id;
    setLoading(true);
    const { error } = await supabase.rpc("god_set_tier", {
      p_business_id: effectiveBusinessId,
      p_tier_id: selectedTierId,
    });
    if (error) {
      setLoading(false);
      showToast(`Failed to set tier: ${error.message}`, "error");
      return;
    }
    // Verify the tier actually changed — catches silent SQL parameter name mismatches
    const { data: sub, error: verifyError } = await supabase
      .from("subscriptions")
      .select("tier_id")
      .eq("business_id", effectiveBusinessId)
      .maybeSingle();
    setLoading(false);
    if (verifyError || !sub) {
      showToast("Tier RPC OK but subscription unverifiable — check god_set_tier param names", "error");
      return;
    }
    if (sub.tier_id !== selectedTierId) {
      showToast(`Tier RPC ran but tier is still '${sub.tier_id}' — check god_set_tier param names`, "error");
      return;
    }
    const tierLabel = TIERS.find((t) => t.id === selectedTierId)?.label || selectedTierId;
    showToast(`✓ ${selectedUser.business_name || selectedUser.full_name} set to ${tierLabel}`, "success");
    setSelectedUser(null);
    setSelectedTierId(null);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <ProfileSearch onSelect={setSelectedUser} selectedUser={selectedUser} />
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {TIERS.filter((t) => t.id !== "trial").map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setSelectedTierId(t.id)}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "9px 12px", borderRadius: "8px", cursor: "pointer",
              background: selectedTierId === t.id ? G.goldFaint : G.surface,
              border: selectedTierId === t.id ? `1px solid ${G.gold}` : `1px solid rgba(255,255,255,0.07)`,
              color: G.text, textAlign: "left",
            }}
          >
            <span style={{ fontSize: "0.83rem", fontWeight: 500 }}>{t.label}</span>
            {selectedTierId === t.id && (
              <span style={{ color: G.gold, fontSize: "0.75rem" }}>✓</span>
            )}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={handleSetTier}
        disabled={loading || !selectedUser || !selectedTierId}
        style={{
          background: loading || !selectedUser || !selectedTierId
            ? "rgba(201,168,76,0.2)"
            : `linear-gradient(135deg, ${G.gold}, ${G.goldBright})`,
          color: loading || !selectedUser || !selectedTierId ? G.goldDim : "#000",
          border: "none", borderRadius: "8px",
          padding: "11px", fontWeight: 700, fontSize: "0.88rem",
          cursor: loading || !selectedUser || !selectedTierId ? "not-allowed" : "pointer",
        }}
      >
        {loading ? "Setting tier…" : "Set tier"}
      </button>
    </div>
  );
}

// ── Section 4: God mode users (static display-only list) ──────────────────────
// Not live data — hardcoded list of accounts with god mode access.
// To add/remove god mode users, update this list and the Supabase RLS policy.
function GodModeUsersSection() {
  const users = ["andrew@yousolar.com.au", "shannon@yousolar.com.au"];
  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "12px" }}>
        {users.map((email) => (
          <div
            key={email}
            style={{
              display: "flex", alignItems: "center", gap: "10px",
              padding: "10px 12px", borderRadius: "8px",
              background: G.surface, border: `1px solid rgba(255,255,255,0.07)`,
            }}
          >
            <span style={{ fontSize: "1rem" }}>🛡️</span>
            <div>
              <div style={{ color: G.text, fontSize: "0.83rem", fontWeight: 500 }}>{email}</div>
              <div style={{ color: G.goldDim, fontSize: "0.72rem", marginTop: "1px" }}>Granted access</div>
            </div>
          </div>
        ))}
      </div>
      <p style={{ color: G.muted, fontSize: "0.73rem", margin: 0, lineHeight: 1.5 }}>
        To add new god mode users, run the SQL in Supabase directly.
      </p>
    </div>
  );
}

// ── Section 5: Quick stats ─────────────────────────────────────────────────────
function QuickStatsSection({ open }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetched = useRef(false);

  const fetchStats = useCallback(async () => {
    if (fetched.current) return;
    fetched.current = true;
    setLoading(true);

    try {
      const [tradiesResult, leadsResult, txResult] = await Promise.all([
        supabase.from("profiles").select("id", { count: "exact", head: true }).eq("role", "tradie"),
        supabase.from("leads").select("id", { count: "exact", head: true }),
        supabase.from("credit_transactions").select("amount").eq("type", "purchase"),
      ]);

      const totalCredits = (txResult.data || []).reduce((sum, row) => sum + (row.amount || 0), 0);

      setStats({
        tradies: tradiesResult.count ?? 0,
        leads: leadsResult.count ?? 0,
        creditsSold: totalCredits,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) fetchStats();
  }, [open, fetchStats]);

  const StatBox = ({ label, value }) => (
    <div style={{
      flex: 1, background: G.surface, border: `1px solid ${G.goldBorder}`,
      borderRadius: "12px", padding: "14px 12px", textAlign: "center",
    }}>
      {loading ? (
        <div style={{
          height: "28px", background: "rgba(255,255,255,0.07)",
          borderRadius: "8px", marginBottom: "6px",
        }} />
      ) : (
        <div style={{ color: G.gold, fontSize: "1.5rem", fontWeight: 800, lineHeight: 1 }}>
          {typeof value === "number" ? value.toLocaleString() : "—"}
        </div>
      )}
      <div style={{ color: G.muted, fontSize: "0.72rem", marginTop: "6px" }}>{label}</div>
    </div>
  );

  return (
    <div style={{ display: "flex", gap: "8px" }}>
      <StatBox label="Total tradies" value={stats?.tradies} />
      <StatBox label="Total leads" value={stats?.leads} />
      <StatBox label="Credits sold" value={stats?.creditsSold} />
    </div>
  );
}

// ── Main GodModePanel ──────────────────────────────────────────────────────────
// Controlled when `open` / `setOpen` are passed; falls back to its own internal
// state for backward compatibility. The legacy floating ⚡ FAB has been
// removed — the panel is now triggered from the Settings page only.
export default function GodModePanel({ subscription, onSimulateTier, session, open: openProp, setOpen: setOpenProp }) {
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp !== undefined ? openProp : openInternal;
  const setOpen = setOpenProp || setOpenInternal;
  const [openSection, setOpenSection] = useState("simulate");

  const simulatedTierId = subscription?.simulatedTierId ?? null;

  function toggleSection(key) {
    setOpenSection((prev) => (prev === key ? null : key));
  }

  return (
    <>
      {/* Pulse keyframes */}
      <style>{`
        @keyframes godPulse {
          0%, 100% { box-shadow: 0 4px 20px rgba(201,168,76,0.5); }
          50%       { box-shadow: 0 4px 32px rgba(201,168,76,0.85), 0 0 0 6px rgba(201,168,76,0.12); }
        }
        @keyframes godSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>

      {/* Overlay */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.7)",
            zIndex: 99997,
          }}
        />
      )}

      {/* Drawer */}
      <div
        style={{
          position: "fixed", top: 0, right: 0,
          height: "100vh", width: "clamp(320px, 380px, 100vw)",
          background: G.bg, borderLeft: `1px solid ${G.goldBorder}`,
          zIndex: 99998, overflowY: "auto",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
          display: "flex", flexDirection: "column",
        }}
      >
        {/* Drawer header */}
        <div style={{
          background: G.bgHeader, borderBottom: `1px solid ${G.goldBorder}`,
          padding: "20px 24px", flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ color: G.gold, fontSize: "1.1rem", fontWeight: 700 }}>⚡ God Mode</div>
            <div style={{ color: G.goldDim, fontSize: "0.75rem", marginTop: "2px" }}>Remote Quote Admin</div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            style={{
              background: "none", border: "none", color: G.gold,
              fontSize: "1.4rem", cursor: "pointer", lineHeight: 1,
              padding: "4px 8px",
            }}
          >
            ×
          </button>
        </div>

        {/* Simulation active banner */}
        {simulatedTierId && (
          <div style={{
            background: "rgba(201,168,76,0.12)", borderBottom: `1px solid ${G.goldBorder}`,
            padding: "8px 20px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <span style={{ color: G.gold, fontSize: "0.78rem", fontWeight: 600 }}>
              ⚡ Simulating: {TIERS.find((t) => t.id === simulatedTierId)?.label || simulatedTierId}
            </span>
            <button
              type="button"
              onClick={() => onSimulateTier(null)}
              style={{ background: "none", border: "none", color: G.goldDim, cursor: "pointer", fontSize: "0.75rem", textDecoration: "underline" }}
            >
              Reset
            </button>
          </div>
        )}

        {/* Accordion sections */}
        <div style={{ flex: 1 }}>
          <AccordionSection
            title="Simulate tier"
            icon="🎭"
            open={openSection === "simulate"}
            onToggle={() => toggleSection("simulate")}
          >
            <SimulateTierSection
              currentSimulatedTierId={simulatedTierId}
              onSimulateTier={onSimulateTier}
            />
          </AccordionSection>

          <AccordionSection
            title="Add credits to user"
            icon="⚡"
            open={openSection === "credits"}
            onToggle={() => toggleSection("credits")}
          >
            <AddCreditsSection />
          </AccordionSection>

          <AccordionSection
            title="Set subscription tier"
            icon="🏷️"
            open={openSection === "tier"}
            onToggle={() => toggleSection("tier")}
          >
            <SetTierSection />
          </AccordionSection>

          <AccordionSection
            title="God mode users"
            icon="🛡️"
            open={openSection === "users"}
            onToggle={() => toggleSection("users")}
          >
            <GodModeUsersSection />
          </AccordionSection>

          <AccordionSection
            title="Quick stats"
            icon="📊"
            open={openSection === "stats"}
            onToggle={() => toggleSection("stats")}
          >
            <QuickStatsSection open={openSection === "stats"} />
          </AccordionSection>
        </div>

        {/* Footer */}
        <div style={{
          padding: "12px 20px", borderTop: `1px solid ${G.goldBorder}`,
          background: "rgba(201,168,76,0.04)", flexShrink: 0,
        }}>
          <p style={{ color: G.muted, fontSize: "0.7rem", margin: 0, textAlign: "center", lineHeight: 1.5 }}>
            God mode · Remote Quote Admin · Session only
          </p>
        </div>
      </div>

      {/* Floating ⚡ FAB removed 2026-04-29 — open the panel from Settings → God Mode. */}
    </>
  );
}
